<template>
  <div>
    <page-content-toolbar
      :projectName="`${project && project.name ? project.name : ' '}`"
      :title="$t('scenario.incompatibleSceenarii')"
    />

    <v-container
      v-if="!loadingScenarios && availableScenarii.length"
      class="px-12 pt-7 pb-1 mt-2"
      fluid
    >
      <scenario-block
        @changeScenario="changeScenario($event)"
        :scenario="currentScenario"
        :scenarios="availableScenarii"
        v-on="$listeners"
      />

      <v-row
        dense
        class="elevation-1 px-4 mx-1 mb-6 py-4 my-5 grey lighten-2"
      >
        <v-col cols="12">
          <h3 class="pa-3">{{ $t('scenario.selectOneOrMultipleIncompatibleScenarii') }}:</h3>
        </v-col>
      </v-row>

      <scenario-block
        v-for="scenario in computedAvailableScenarii"
        :key="scenario.id"
        :scenario="scenario"
        selectable
        @click.native="selectScenario(scenario)"
        :selected="currentScenarioIncompatibleList.indexOf(scenario.id) > -1"
        class="mb-6 rai-selectable-scenario"
        v-on="$listeners"
        :enableResolvedCheckMark="false"
      />

      <v-row
        no-gutters
        justify="center"
        class="pt-2"
      >
        <raison-button
          :button-label="$t('scenario.nextScenario')"
          :button-function="validateIncompatibleScenarii"
          :loading="callingApi"
          :disabled="noMoreNextScenario"
        />
      </v-row>

    </v-container>
    <v-progress-circular
      v-else-if="loadingScenarios"
      size="150"
      width="5"
      class="d-block mx-auto"
      indeterminate
      color="primary"
    />
    <div v-else class="ma-5 text--center">
      {{ $t('scenario.noScenarioFound') }}
    </div>
    
    <footer-navigation
      :prev-button-function="backToIdentifyContradictoryOptions"
      prev-button-label="option.identifyContradictoryOptions"
      :next-button-function="goToScenariiList"
      next-button-label="project.knowledgeRepresentation"
    />

  </div>
</template>

<script>
import PageContentToolbar from '@/components/layout/PageContentToolbar';
import RaisonButton from '@/components/global/RaisonButton';
import ScenarioBlock from '@/components/global/ScenarioBlock';
import FooterNavigation from '@/components/layout/FooterNavigation';

import projectPageMixin from '@/mixins/project-page-mixin.js';

import scenarioService from '@/services/scenarios.js';

export default {
  name: 'IncompatibleScenarii',

  components: { PageContentToolbar, RaisonButton, ScenarioBlock, FooterNavigation },

  mixins: [ projectPageMixin ],

  data () {
    return {
      currentScenario: undefined,
      availableScenarii: [],
      callingApi: false,
      loadingScenarios: false,
      incompatibleList: [],
    };
  },

  computed: {
    noMoreNextScenario () {
      return this.currentScenarioIndex >= this.availableScenarii.length - 1
    },

    currentScenarioIndex() {
      const isCurrentScenario = (scenario) => scenario.id === this.currentScenario.id
      return this.availableScenarii.findIndex(isCurrentScenario)
    },

    computedAvailableScenarii () {
      return this.availableScenarii.filter(sce => sce.id !== this.currentScenario.id);
    },

    currentScenarioIncompatibleList () {
      let toReturn = [];
      this.incompatibleList.forEach(inc => {
        if (this.currentScenario.id === inc.scenario.id) {
          toReturn.push(inc.scenarioincompatible.id);
        }
        if (this.currentScenario.id === inc.scenarioincompatible.id) {
          toReturn.push(inc.scenario.id);
        }
      });
      return toReturn;
    },
  },

  methods: {
    reinit () {
      this.callingApi = false;
      this.availableScenarii.forEach(sce => {  // TODO: when connected to api, we(ll either call again, or store it differently
        sce.selected = false;
      })
    },

    changeScenario (scenario) {
      this.currentScenario = scenario;
      this.reinit();
    },

    validateIncompatibleScenarii () {
      this.currentScenario = this.availableScenarii[this.currentScenarioIndex + 1]
    },

    selectScenario (scenario) {
      this.$set(scenario, 'selectLoading', true);
      let incompatible = this.incompatibleList
        .find(inc => inc.scenario.id === this.currentScenario.id && inc.scenarioincompatible.id === scenario.id);
      if (incompatible) {
        scenarioService.deleteIncompatibleScenario(this.$route.params.id, incompatible.id).then(() => {
          let idx = this.incompatibleList.indexOf(incompatible);
          this.incompatibleList.splice(idx, 1);
        }).catch(err => {
          this.$emit('raison-event', {type: 'error', messages: [err] });
        }).finally(() => {
          this.$set(scenario, 'selectLoading', false);
        });
      } else {
        let newIncompatible = {
          "scenario": this.currentScenario,
          "scenarioincompatible": scenario
        };

        scenarioService.createIncompatibleScenario(this.$route.params.id, newIncompatible).then(res => {
          this.incompatibleList.push(res.data);
        }).catch(err => {
          this.$emit('raison-event', { type: 'error', messages: [err] });
        }).finally(() => {
          this.$set(scenario, 'selectLoading', false);
        });
      }
    },

    loadScenarios() {
      this.loadingScenarios = true;
      scenarioService.getMinimalScenarios(this.$route.params.id).then(res => {
        let responseScenarios = res.data;
        this.availableScenarii = responseScenarios.filter(sce => sce.level === 1);
        if (this.availableScenarii.length > 0) {
          if (this.$route.params.scenarioId) {
            this.currentScenario = this.availableScenarii.find(scenario => scenario.id === this.$route.params.scenarioId);
          } else {
            this.currentScenario = this.availableScenarii[0];
          }
        }
      }).catch(err => {
        this.$emit('raison-event', { type: 'error', messages: [err] });
      }).finally(() => {
        this.loadingScenarios = false;
      })
    },

    loadIncompatibles() {
      return new Promise((resolve, reject) => {
        scenarioService.getIncompatibleScenarios(this.$route.params.id).then(res => {
          this.incompatibleList = res.data;
          resolve();
        }).catch(err => {
          this.$emit('raison-event', { type: 'error', messages: [err] });
          reject();
        })
      })
    },

    backToIdentifyContradictoryOptions () {
      this.$router.push({name: 'identifyContradictoryOptions', params: { id: this.$route.params.id }});
    },

    goToScenariiList () {
      this.$router.push({name: 'scenariiList', params: { id: this.$route.params.id } });
    },
  },

  created () {
    this.loadIncompatibles().then(() => {
      this.loadScenarios();
    });
  },
};
</script>

<style>
.rai-selectable-scenario {
  cursor: pointer;
}
</style>
